import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import CarriersShowcase from '../components/ui/CarriersShowcase';

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  suffix = '' 
}) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * end));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const Carriers: React.FC = () => {
  return (
    <div className="relative min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          {/* Hero Content */}
          <div className="max-w-3xl mb-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5 leading-tight">
              Carrier access that actually matters.
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Here's the thing. You can have the best sales skills in the room, but if you're stuck with one or two carriers,
              you're going to lose deals. Client doesn't qualify? Can't compete on price? No secondary options? You just lost that sale.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              We've spent years building relationships with 28+ carriers so you don't have to. When one carrier says no, 
              you've got 19 more to try. That's not a nice-to-have. That's how you close deals others can't.
            </p>
          </div>

          {/* Stats Row - Centered with Slashes */}
          <div className="flex flex-wrap items-center justify-center gap-y-3 text-center">
            <div className="px-4">
              <span className="text-2xl font-bold text-navy-800"><AnimatedCounter end={28} suffix="+" /></span>
              <span className="text-sm text-gray-500 ml-1">Carriers</span>
            </div>
            <span className="text-gray-300 hidden sm:inline">/</span>
            <div className="px-4">
              <span className="text-2xl font-bold text-navy-800"><AnimatedCounter end={150} suffix="+" /></span>
              <span className="text-sm text-gray-500 ml-1">Products</span>
            </div>
            <span className="text-gray-300 hidden sm:inline">/</span>
            <div className="px-4">
              <span className="text-2xl font-bold text-navy-800"><AnimatedCounter end={50} suffix="+" /></span>
              <span className="text-sm text-gray-500 ml-1">States</span>
            </div>
            <span className="text-gray-300 hidden sm:inline">/</span>
            <div className="px-4">
              <span className="text-2xl font-bold text-navy-800"><AnimatedCounter end={1000} duration={2500} suffix="+" /></span>
              <span className="text-sm text-gray-500 ml-1">Agents</span>
            </div>
            <span className="text-gray-300 hidden sm:inline">/</span>
            <div className="px-4">
              <span className="text-2xl font-bold text-navy-800"><AnimatedCounter end={8} suffix="+" /></span>
              <span className="text-sm text-gray-500 ml-1">Years</span>
            </div>
            <span className="text-gray-300 hidden sm:inline">/</span>
            <div className="px-4">
              <span className="text-2xl font-bold text-navy-800">24/7</span>
              <span className="text-sm text-gray-500 ml-1">Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Carrier Showcase */}
      <section className="py-12 bg-gray-50">
        <CarriersShowcase />
      </section>

      {/* Why This Matters Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
            Why carrier access is your biggest advantage.
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Every client is different.
                </h3>
                <p className="text-slate-600">
                  A 55-year-old diabetic isn't getting the same rate as a healthy 35-year-old. 
                  Having 28+ carriers means you can find competitive options for real people with real health histories,
                  not just the easy cases.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Price shopping is real.
                </h3>
                <p className="text-slate-600">
                  Your clients are comparing quotes. If you only have access to a few carriers, you're probably not winning on price. 
                  More carriers = more chances to beat the competition.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Declines happen. Then what?
                </h3>
                <p className="text-slate-600">
                  When a carrier declines your client, do you give up? Or do you have 19 other options to try? 
                  The agents making real money always have a Plan B. And C. And D.
                </p>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Different products for different needs.
                </h3>
                <p className="text-slate-600">
                  Final expense, term, whole life, IUL, annuities. Your clients have different goals.
                  Our carrier portfolio covers it all, so you're never turning business away.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  We did the vetting.
                </h3>
                <p className="text-slate-600">
                  Not all carriers are created equal. We work with financially stable carriers that pay claims and treat agents fairly. 
                  You're not gambling with your reputation.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Contracting without the headache.
                </h3>
                <p className="text-slate-600">
                  Getting appointed with 28 carriers on your own? That's weeks of paperwork. 
                  Through us, you're up and running with our entire portfolio fast.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Carrier List Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            The carriers you'll have access to.
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Life, final expense, IUL, annuities, all from carriers with strong financials and solid reputations.
            This is the network that took years to build.
          </p>
          
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                'Mutual of Omaha',
                'Americo', 
                'American Home Life',
                'Combined',
                'F&G',
                'Royal Neighbors',
                'American Amicable',
                'Liberty Bankers',
                'Transamerica',
                'Corebridge',
                'SBLI',
                'National Life Group',
                'Aetna',
                'Aflac',
                'GTL',
                'United Home Life',
                'Foresters',
                'Athene',
                'Kansas City Life',
                'Allianz'
              ].map((carrier) => (
                <span
                  key={carrier}
                  className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-navy-50 hover:border-navy-200 hover:text-navy-800 transition-colors cursor-default"
                >
                  {carrier}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`
              }}
            />
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-navy-900/70" />
            
            {/* Glass Container */}
            <div className="relative backdrop-blur-sm bg-white/5 border border-white/10 p-10 sm:p-14 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Stop losing deals to carrier limitations.
              </h2>
              <p className="text-white/80 mb-8 max-w-2xl mx-auto">
                Get access to 28+ carriers, 150+ products, and the support to actually use them. 
                This is what separates agents who struggle from agents who scale.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/apply"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-navy-800 font-semibold rounded-xl hover:bg-slate-100 transition-colors shadow-lg"
                >
                  Get Carrier Access
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
                >
                  Talk to Us First
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Carriers;
